import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useBreakpointValue } from '@chakra-ui/react';
import { useSettings } from '../../stateManagement/SettingsContext';
import { useAuth } from '../../stateManagement/auth/useAuth';

const barStyle: React.CSSProperties = {
  width: '22px',
  height: '2px',
  backgroundColor: '#0B1E3A',
  margin: '4px 0',
  borderRadius: '2px',
};

const baseMenuItemStyle: React.CSSProperties = {
  padding: '10px 12px',
  fontSize: '14px',
  borderRadius: '6px',
  cursor: 'pointer',
  textAlign: 'left',
  width: '100%',
  border: 'none',
  background: 'transparent',
};

type NavConfigItem = {
  key: string;
  path: string;
  toggleVisibility?: boolean;
};

interface HamburgerMenuProps {
  loginButtonText?: string;
  contactUsButtonText?: string;
  onButtonClick?: () => void;
}

interface MenuItem {
  id: string;
  label: string;
  path?: string; // only for navigation
  action?: () => void; // login/logout/contact
  toggleVisibility?: boolean;
}

const capitalizeFirstLetter = (str: string) =>
  str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();

const HamburgerMenu: React.FC<HamburgerMenuProps> = ({
  loginButtonText,
  contactUsButtonText,
  onButtonClick,
}) => {
  const { settings, hamburgerMenuLinks } = useSettings();
  const { isAuthenticated, handleLogin, handleLogout } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { t } = useTranslation();

  const isMobileOrTablet = useBreakpointValue({ base: true, lg: false }) ?? true;
  const navConfig: NavConfigItem[] = hamburgerMenuLinks?.navConfig ?? [];

  /* ---------- Close menu on scroll ---------- */
  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const handleScroll = () => {
      setIsOpen(false);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [isOpen]);

  /* ---------- Build menu items ---------- */

  const menuItems: MenuItem[] = navConfig.map(item => ({
    id: item.key,
    label: t(`data.navLinks.${item.key}`),
    path: item.path,
    toggleVisibility: item.toggleVisibility !== false,
  }));

  if (isMobileOrTablet) {
    menuItems.push(
      {
        id: 'contact-us',
        label: capitalizeFirstLetter(contactUsButtonText || 'Contact Us'),
        action: () => {
          onButtonClick?.();
        },
      },
      {
        id: 'auth-button',
        label: capitalizeFirstLetter(isAuthenticated ? 'LOGOUT' : loginButtonText || 'LOGIN'),
        action: () => {
          if (isAuthenticated) handleLogout();
          else handleLogin();
        },
      },
    );
  }

  const visibleMenuItems = menuItems.filter(
    item => item.toggleVisibility !== false && (item.path || item.action),
  );

  if (visibleMenuItems.length === 0) {
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.warn('HamburgerMenu: No valid menu items found');
    }
    return null;
  }

  /* ---------- Position menu ---------- */

  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setMenuPos({
        top: rect.bottom + window.scrollY,
        left: rect.right - 220,
      });
    }
  }, [isOpen]);

  /* ---------- Close on outside click ---------- */

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(e.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  /* ----------  CLOSE ON ESC KEY ---------- */
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  return (
    <>
      {/* Hamburger Button */}
      <button
        ref={buttonRef}
        type="button"
        aria-label="Menu"
        aria-expanded={isOpen}
        onClick={() => setIsOpen(prev => !prev)}
        style={{
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          padding: '8px',
        }}
      >
        <div aria-hidden="true">
          <div style={barStyle} />
          <div style={barStyle} />
          <div style={barStyle} />
        </div>
      </button>

      {/* Hamburger Menu */}
      {isOpen &&
        createPortal(
          <div
            ref={menuRef}
            style={{
              position: 'absolute',
              top: menuPos.top,
              left: menuPos.left,
              width: '220px',
              background: settings?.configs.primaryBgColor,
              borderRadius: '10px',
              padding: '8px',
              zIndex: 2147483647,
              boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
            }}
          >
            {menuItems
              .filter(item => item.toggleVisibility !== false)
              .map(item => {
                const isHovered = hoveredItem === item.label;

                /* -------- ACTION ITEMS (buttons) -------- */
                if (item.action) {
                  return (
                    <button
                      key={item.id}
                      type="button"
                      aria-label={item.label}
                      onMouseEnter={() => setHoveredItem(item.label)}
                      onMouseLeave={() => setHoveredItem(null)}
                      onClick={() => {
                        setIsOpen(false);
                        item.action?.();
                      }}
                      style={{
                        ...baseMenuItemStyle,
                        border: 'none',
                        background: isHovered
                          ? settings?.configs.gradientAccentColor ||
                            'linear-gradient(to right, #4272D4, #8934C4)'
                          : 'transparent',
                        color: isHovered ? '#ffffff' : settings?.configs.primaryTextColor,
                        fontWeight: isHovered ? '600' : '400',
                      }}
                    >
                      {item.label}
                    </button>
                  );
                }

                /* -------- NAV ITEMS (anchors) -------- */
                const safePath = item.path && item.path.trim() !== '' ? item.path : '/';
                return (
                  <a
                    key={item.id}
                    href={safePath}
                    style={{ textDecoration: 'none' }}
                    onMouseEnter={() => setHoveredItem(item.label)}
                    onMouseLeave={() => setHoveredItem(null)}
                    onClick={e => {
                      if (!e.metaKey && !e.ctrlKey) {
                        e.preventDefault();
                        setIsOpen(false);
                        navigate(item.path!);
                      }
                    }}
                  >
                    <div
                      style={{
                        ...baseMenuItemStyle,
                        background: isHovered
                          ? settings?.configs.gradientAccentColor ||
                            'linear-gradient(to right, #4272D4, #8934C4)'
                          : 'transparent',
                        color: isHovered ? '#ffffff' : settings?.configs.primaryTextColor,
                        fontWeight: isHovered ? '600' : '400',
                      }}
                    >
                      {item.label}
                    </div>
                  </a>
                );
              })}
          </div>,
          document.body,
        )}
    </>
  );
};

export { HamburgerMenu };
